import React, { useRef, useState, useEffect } from "react";
import filterIcon from "../../assets/filtler.svg";

export const SORT_OPTIONS = [
  { value: "date-desc", label: "Date new to old" },
  { value: "date-asc", label: "Date old to new" },
] as const;

export type SortOptionValue = (typeof SORT_OPTIONS)[number]["value"];

interface ReportSortBarProps {
  value: SortOptionValue;
  onChange: (value: SortOptionValue) => void;
}

const ReportSortBar: React.FC<ReportSortBarProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLabel =
    SORT_OPTIONS.find((o) => o.value === value)?.label ?? "Date new to old";

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleSelect = (val: SortOptionValue) => {
    onChange(val);
    setOpen(false);
  };

  const toggleDropdown = () => setOpen((prev) => !prev);

  return (
    <div
      ref={containerRef}
      className="report-sort-bar relative inline-flex min-w-0  w-full max-w-[374px] xl:my-7 my-4"
    >
      <div className="inline-flex items-stretch min-w-0 w-full rounded-[9999px] border border-[#868686]   overflow-hidden ">
        <div className="report-sort-bar__label   items-center rounded-full w-[126px] flex justify-center bg-[#E761A0] shrink-0">
          <span className="text-white font-semibold text-sm uppercase tracking-wide whitespace-nowrap">
            SORT BY
          </span>
        </div>
        <button
          type="button"
          onClick={toggleDropdown}
          className="report-sort-bar__select-wrap flex-1 flex items-center justify-between gap-3 pl-4 pr-3 py-2.5 sm:py-[14px] min-w-0 bg-transparent text-center   focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#E761A0] rounded-r-[9999px] outline-none"
          aria-label="Sort reports by"
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="flex-1 min-w-0 text-white font-normal text-sm sm:text-base truncate">
            {currentLabel}
          </span>
          <img
            src={filterIcon}
            alt=""
            className="report-sort-bar__icon w-5 h-5 sm:w-6 sm:h-5 shrink-0 pointer-events-none"
            aria-hidden
          />
        </button>
      </div>

      {open && (
        <ul
          className="absolute top-full left-0 right-0 mt-1 z-50 min-w-[200px] rounded-lg border border-[#868686] bg-[#1a1a1a] py-1 shadow-lg list-none"
          role="listbox"
          aria-label="Sort options"
        >
          {SORT_OPTIONS.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={value === opt.value}
            >
              <button
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left px-4 py-2.5 text-sm sm:text-base focus:outline-none focus:bg-white/10 hover:bg-white/10 ${value === opt.value
                  ? "text-[#E761A0] font-medium bg-white/5"
                  : "text-white"
                  }`}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReportSortBar;
